import { useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Button, LoadingState } from "@odingard/design-system";
import { useAuth } from "../lib/auth";
import { GLOBAL_NAV, ORGANIZATION_NAV } from "../shell/applications";
import { AppSwitcher } from "../shell/AppSwitcher";
import { AttentionIndicator } from "../shell/AttentionIndicator";
import { ContextIndicator } from "../shell/ContextIndicator";
import { CommandPalette } from "../shell/CommandPalette";
import { GlobalSearch } from "../shell/GlobalSearch";
import { useShell } from "../shell/useShell";

export function StudioLayout() {
  const { user, loading, activeOrg, organizations, setActiveOrg, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const orgId = activeOrg?.id ?? "";
  const shell = useShell(orgId, location.pathname);
  const current = shell.current;
  const currentApp = shell.applications.find((a) => a.manifest.id === current?.id);

  if (loading) return <LoadingState label="Loading your workspace…" />;
  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  // A signed-in user with no organization has nothing to scope Studio requests
  // to (every org-scoped call would hit /organizations//… and 404). Send them
  // to finish creating their organization instead of rendering broken pages.
  if (organizations.length === 0) {
    return <Navigate to="/start" replace />;
  }

  return (
    <div className="studio">
      <aside id="studio-sidebar" className={`studio__sidebar ${open ? "studio__sidebar--open" : ""}`}>
        <Link to="/studio" className="studio__brand">
          <img className="brand__logo" src="/odingard-logo.png" alt="Odingard" /> <span className="brand__suffix">Studio</span>
        </Link>
        <nav className="studio__nav" aria-label="Studio">
          {GLOBAL_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end ?? false} onClick={() => setOpen(false)}>
              <span aria-hidden="true">{n.icon}</span> {n.label}
            </NavLink>
          ))}
          {/* The current application's navigation, and only its own. Every
              application at once was the old scheme, and at nine applications it
              listed 57 links to one customer (ODS-EWO-033 P1). */}
          {/* And only when the customer can enter it. A path is not an
              entitlement: an organization without the add-on that opens a
              governance URL is refused by the route, and must not be handed a
              menu of the rest of it. When the platform did not answer at all,
              navigation stays rather than disappearing, because every route
              still checks for itself. */}
          {current && (currentApp?.canEnter || !shell.entitlementsKnown) && (
            /* The application's own links are a named group, so a screen reader
               says whose navigation this is rather than reading eight
               unattributed links (ODS-EWO-033 P6). */
            <div className="studio__nav-group" role="group" aria-labelledby="studio-nav-app">
              <div className="studio__nav-app" id="studio-nav-app">
                <span aria-hidden="true">{currentApp?.icon ?? current.icon}</span>{" "}
                {currentApp?.name ?? current.name}
              </div>
              {current.nav.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end ?? false} onClick={() => setOpen(false)}>
                  <span aria-hidden="true">{n.icon}</span> {n.label}
                </NavLink>
              ))}
            </div>
          )}
          <div className="studio__nav-label">Organization</div>
          {ORGANIZATION_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}>
              <span aria-hidden="true">{n.icon}</span> {n.label}
            </NavLink>
          ))}
          <div className="studio__nav-label">Discover</div>
          <NavLink to="/marketplace" onClick={() => setOpen(false)}>
            <span aria-hidden="true">🛍️</span> Browse marketplace
          </NavLink>
          {user.platformRole === "platform_admin" && (
            <>
              <div className="studio__nav-label">Platform</div>
              <NavLink to="/studio/catalog-admin" onClick={() => setOpen(false)}>
                <span aria-hidden="true">🗄️</span> Catalog Admin
              </NavLink>
            </>
          )}
        </nav>
      </aside>

      <div className="studio__main">
        <div className="studio__topbar">
          <button
            className="studio__sidebar-toggle ods-btn ods-btn--ghost ods-btn--sm"
            aria-label="Toggle sidebar"
            aria-expanded={open}
            aria-controls="studio-sidebar"
            onClick={() => setOpen((o) => !o)}
          >
            ☰
          </button>
          <AppSwitcher shell={shell} />
          <GlobalSearch orgId={orgId} />
          <CommandPalette shell={shell} platformAdmin={user.platformRole === "platform_admin"} />
          <ContextIndicator orgId={orgId} />
          <AttentionIndicator orgId={orgId} />
          {organizations.length > 0 && (
            <label className="org-switcher">
              <span aria-hidden="true">🏢</span>
              <select
                aria-label="Active organization"
                value={activeOrg?.id ?? ""}
                onChange={(e) => setActiveOrg(e.target.value)}
                style={{ border: "none", background: "transparent", font: "inherit", fontWeight: 600 }}
              >
                {organizations.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "var(--ods-space-3)" }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                await logout();
                navigate("/");
              }}
            >
              Sign out
            </Button>
            <span className="avatar" title={user.displayName}>
              {user.displayName.slice(0, 2).toUpperCase()}
            </span>
          </div>
        </div>
        <div className="studio__content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
